import Tag from '../../ui/Tag';
import Toggle from '../../ui/Toggle';
import {ButtonColour} from '../../ui/ButtonColour';
import IconList from '../icon/IconList';
import Tooltip from '../../ui/Tooltip';
import IconInfoLight from '../icon/IconInfoLight';
import IconEmail from '../icon/IconEmail';
import IconDocumentEmpty from '../icon/IconDocumentEmpty';
import IconEmptyCalendar from '../icon/IconEmptyCalendar';
import PrimaryButton from '../../ui/PrimaryButton';
import React from 'react';
import {LicenseType} from '../../schema';
import If from '../utils/If';
import IconDocument from '../icon/IconDocument';
import HashTag from '../../ui/HashTag';
import IconCalendar from '../icon/IconCalendar';
import {reverse} from '../../urls';
import gql from 'graphql-tag';
import {useMutation} from '@apollo/client';
import {handleError, handleSuccess} from '../../utils/Apollo';
import Modal from '../../ui/modal/Modal';
import ModalTitle from '../../ui/modal/ModalTitle';
import ModalContent from '../../ui/modal/ModalContent';
import ModalButtons from '../../ui/modal/ModalButtons';
import TextButton from '../../ui/TextButton';
import UILink from '../../ui/UILink';

type Props = {
  licenseType: LicenseType
  types: Map<string, LicenseType>
  canDelete: boolean
  numberOfEventTypes: number
}

export default function LicenseTypeTile({licenseType, types, canDelete, numberOfEventTypes}: Props) {
  const [showDelete, setShowDelete] = React.useState(false);

  const [toggle] = useMutation(toggleMutation, {
    refetchQueries: ['GetLicenseTypesQuery', 'licenseTypes']
  });
  const [deleteMe] = useMutation(deleteMutation, {
    refetchQueries: ['GetLicenseTypesQuery', 'licenseTypes']
  });

  const onClose = () => {
    setShowDelete(false);
  }

  const onDelete = React.useCallback(() => {
    deleteMe({
      variables: {id: licenseType.id}
    }).then(
      () => handleSuccess('License type deleted'),
      (apolloError) => handleError(apolloError)
    );
  }, [deleteMe, licenseType]);


  const onToggle = React.useCallback(() => {
    toggle({
      variables: {id: licenseType.id}
    }).then(
      () => handleSuccess(`License type ${licenseType.active ? 'deactivated' : 'activated'}`),
      (apolloError) => handleError(apolloError)
    );
  }, [toggle, licenseType]);


  return (
    <div className="bg-gray-light py-5 px-5 mb-4 flex flex-col justify-between">
      <div>
        <If condition={canDelete || licenseType.tags.length > 0}>
          <div className="flex justify-between mb-4 items-start">
            <div>
              {licenseType.tags.map((tag, index) => (
                <HashTag key={index} className="uppercase font-medium text-xs">{tag}</HashTag>
              ))}
            </div>
            <If condition={canDelete}>
              <Toggle colour={ButtonColour.BLUE} active={true} on={licenseType.active} handleStateSwitch={onToggle}/>
            </If>
          </div>
        </If>
        <div className="text-xl mb-6">
          {licenseType.name}
          {licenseType.parentLicenseTypeId && types.has(licenseType.parentLicenseTypeId) &&
            <div className="text-red border border-red rounded-md mt-0.5 px-1 py-0.5 text-xs"
              style={{width: "fit-content"}}>
              {types.get(licenseType.parentLicenseTypeId)?.name} add-on
            </div>
          }
        </div>
        <div className="mb-4">
          <div className="flex justify-between mb-4">
            <div>
              <IconList className="text-gray-dark -mb-0.5" height="1.25rem"/> Event types
              <Tooltip content={<div className="w-64">When trainers get a license of this license type,
                they receive access to events of these event types. The trainer loses access when the license
                expires.</div>}
                       label="event_types" className="inline-block -mb-1 ml-1"><IconInfoLight
                className="text-gray" height="1.25rem"/></Tooltip>
            </div>
            <If condition={numberOfEventTypes === licenseType.eventTypes.length}>
              <Tag>All types</Tag>
            </If>
            <If condition={numberOfEventTypes !== licenseType.eventTypes.length}>
              <div className="flex flex-col items-end">
                {licenseType.eventTypes.slice(0, 4).map((eventType) => (
                  <Tag key={eventType.id} className="mb-2">{eventType.name}</Tag>
                ))}
                <If condition={licenseType.eventTypes.length > 4}>
                  <span className="mb-2 text-gray-dark text-sm pr-2">and {licenseType.eventTypes.length - 4} more</span>
                </If>
              </div>
            </If>
          </div>
          <div className="flex justify-between mb-4">
            <div>
              <IconEmail className="text-gray-dark -mb-0.5" height="1.25rem"/> Renewal notifications
              <Tooltip
                content={<div className="w-64">Workshop Butler helps you to decrease a churn by sending up to 5
                  email notifications to a trainer before and after license expiration date to motivate them
                  to renew.</div>}
                label="renewal_notifications" className="inline-block -mb-1 ml-1"><IconInfoLight
                className="text-gray" height="1.25rem"/></Tooltip>
            </div>
            <Tag>
              {licenseType.renewalNotifications.length} {licenseType.renewalNotifications.length === 1 ? 'message' : 'messages'}
            </Tag>
          </div>
          <If condition={!!licenseType.agreement}>
            <div className="flex justify-between mb-4">
              <div>
                <IconDocument className="text-gray-dark" height="1.25rem"/> License agreement
                <LicenseAgreementTooltip id={licenseType.id}/>
              </div>
              <Tag>Version {licenseType.agreement?.version}</Tag>
            </div>
            <div className="flex justify-between mb-4">
              <div>
                <IconCalendar className="text-gray-dark -mt-0.5" height="1.25rem"/> Renewal period
                <RenewalPeriodTooltip id={licenseType.id}/>
              </div>
              <Tag>
                <If condition={!!licenseType.renewalPeriod}>
                  {licenseType.renewalPeriod} days
                </If>
                <If condition={!licenseType.renewalPeriod}>
                  Unlimited
                </If>
              </Tag>
            </div>
          </If>
        </div>
        <If condition={!licenseType.agreement}>
          <div className="border-t border-gray pt-4">
            <p className="mb-4">
              Trainers can renew their licenses without your involvement! Add a license agreement and customize
              the renewal period.
            </p>
            <div className="flex justify-between mb-4">
              <div>
                <IconDocumentEmpty className="text-gray-dark -mb-0.5" height="1.25rem"/> License agreement
                <LicenseAgreementTooltip id={licenseType.id}/>
              </div>
              <UILink href={reverse('license_agreement_add', {licenseTypeId: licenseType.id})}>Add</UILink>
            </div>
            <div className="flex justify-between mb-4 text-gray">
              <div>
                <IconEmptyCalendar className="text-gray -mb-0.5" height="1.25rem"/> Renewal period
                <RenewalPeriodTooltip id={licenseType.id}/>
              </div>
            </div>
          </div>
        </If>
      </div>
      <div className="mt-6 flex flex-row justify-between">
        <PrimaryButton to={reverse('license_type_edit', {id: licenseType.id})}>Edit license type</PrimaryButton>
        <TextButton onClick={() => setShowDelete(true)} colour={ButtonColour.RED}>Delete</TextButton>
      </div>
      <Modal show={showDelete} onClose={onClose}>
        <ModalTitle title={'Delete license type'} onClose={onClose}/>
        <ModalContent>
          <p>Are you sure you want to proceed?</p>
        </ModalContent>
        <ModalButtons>
          <PrimaryButton onClick={onDelete}>Delete</PrimaryButton>
          <TextButton onClick={onClose}>Cancel</TextButton>
        </ModalButtons>
      </Modal>
    </div>
  )
}

function LicenseAgreementTooltip({id}: { id: string }) {
  return (
    <Tooltip content={<div className="w-64">When trainers try to renew their expiring licenses inside
      Workshop Butler, they need to accept a license agreement. By giving their consent, they agree
      with the terms of the agreement. <br/><br/>
      Without the agreement, trainers are not allowed to renew licenses. You have to do it for them.</div>}
             label={`${id}_license_agreement`}
             className="inline-block -mb-1 ml-1"><IconInfoLight
      className="text-gray" height="1.25rem"/></Tooltip>
  );
}

function RenewalPeriodTooltip({id}: { id: string }) {
  return (
    <Tooltip content={<div className="w-64">You can limit a period of time after license expiration,
      during which you allow trainers to renew their licenses. After the renewal period is over,
      Workshop Butler notifies a trainer that they must reach you in order to renew their license. </div>}
             label={`${id}_renewal_period`}
             className="inline-block -mb-1 ml-1"><IconInfoLight
      className="text-gray" height="1.25rem"/></Tooltip>
  );
}


const deleteMutation = gql`
  mutation DeleteLicenseType($id: ID!) {
    deleteLicenseType(id: $id) {
      id
    }
  }
`;

const toggleMutation = gql`
  mutation ToggleTypeMutation($id: ID!) {
    toggleLicenseType(id: $id) {
      id
      active
    }
  }
`;
